import path from 'node:path';
import url from 'node:url';
import type { NodeDef, WorkflowDefinition } from '../types';

export interface DefineWorkflowOptions {
  name: string;
  description?: string;
  owner: { slug: string };
  retain?: { publishedVersions?: number };
  nodes: NodeDef[];
  transitions?: WorkflowDefinition['transitions'];
  /**
   * Escape hatch: explicit module directory used as the base for
   * `promptTemplate` paths. The default is to capture the calling
   * module's path via an `Error().stack` walk; supply this when the
   * builder runs through a bundler that rewrites source positions.
   */
  __dirname?: string;
}

export function defineWorkflow(opts: DefineWorkflowOptions): WorkflowDefinition {
  const sourcePath = opts.__dirname ?? captureCallerPath();
  const def: WorkflowDefinition = {
    name: opts.name,
    owner: opts.owner,
    nodes: opts.nodes,
    __sourcePath: sourcePath,
  };
  if (opts.description !== undefined) def.description = opts.description;
  if (opts.retain !== undefined) def.retain = opts.retain;
  if (opts.transitions !== undefined) def.transitions = opts.transitions;
  return def;
}

/**
 * Walk `Error().stack` to find the calling module's filesystem path
 * (the file that invoked `defineWorkflow`). Stack trace lines look
 * like `    at Object.<anonymous> (/abs/path/file.ts:7:5)`. The
 * implementation finds the first frame whose path is not this
 * package's own source files.
 *
 * Sensitive to bundlers; consuming services run their seed scripts
 * through `tsx` (not bundled) per the Sift conventions.
 */
function captureCallerPath(): string {
  const err = new Error();
  const stack = err.stack ?? '';
  const lines = stack.split('\n');

  const selfMarkers = ['/packages/loom/src/', '/packages/loom/dist/'];

  for (const line of lines) {
    const match = /\s+at\s+(?:.*?\s+)?\(?(.*?):\d+:\d+\)?$/.exec(line);
    if (!match) continue;
    let filePath = match[1];
    if (!filePath) continue;
    if (filePath.startsWith('file://')) {
      filePath = url.fileURLToPath(filePath);
    }
    if (!path.isAbsolute(filePath)) continue;
    if (selfMarkers.some((m) => filePath.includes(m))) continue;
    if (filePath.endsWith('define-workflow.ts') || filePath.endsWith('define-workflow.js')) continue;
    return path.dirname(filePath);
  }
  return process.cwd();
}
